import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import SEO from "components/SEO";
import CanonicalTags from "components/CanonicalTags";
import BrandedSplash from "components/BrandedSplash";
// Homepage stays EAGERLY imported so it lives in the entry chunk and paints
// on first load with no extra round-trip — it is the overwhelming-majority
// landing route. Every other page tree below is code-split via React.lazy so
// a homepage visitor no longer downloads invest/admin/pitch-deck/charts code.
import Homepage from "./pages/homepage";
import {
  routeLoaders,
  installLinkPrefetch,
  installViewportPrefetch,
  prefetchHighIntentWhenIdle,
} from "./lib/routePrefetch";

// Lazy route components. Each is built from the SAME loader the prefetcher
// uses (src/lib/routePrefetch.js) so a hover-prefetch and the eventual render
// resolve to one shared chunk. NotFound is tiny and rarely hit, so it gets its
// own inline lazy loader rather than a prefetch registry entry.
const NotFound = lazy(() => import("pages/NotFound"));
// Referral share-link redirect (/r/:code → /?ref=<code>). Dynamic-param route,
// so — like /blog/:slug — it takes an inline lazy loader rather than a
// routeLoaders registry entry (the prefetcher keys on concrete pathnames and
// safely no-ops on /r/* hrefs). Tiny chunk, hit only from shared links.
const ReferralRedirect = lazy(() => import("./pages/referral-redirect"));
const TermsOfService = lazy(routeLoaders['/terms-of-service']);
const PrivacyPolicy = lazy(routeLoaders['/privacy-policy']);
const PrivacyChoices = lazy(routeLoaders['/privacy-choices']);
const ForContractorsPage = lazy(routeLoaders['/for-contractors']);
const FinancingOptionsPage = lazy(routeLoaders['/financing-options']);
const InstantEstimate = lazy(routeLoaders['/instant-estimate']);
const SupportPage = lazy(routeLoaders['/support']);
const Reviews = lazy(routeLoaders['/reviews']);
const HowItWorks = lazy(routeLoaders['/how-it-works']);
const Gallery = lazy(routeLoaders['/gallery']);
const WarrantyPage = lazy(routeLoaders['/warranty']);
const ShinglesPage = lazy(routeLoaders['/shingles']);
const AboutUs = lazy(routeLoaders['/about']);
const UserRegistration = lazy(routeLoaders['/user-registration']);
const UserDashboard = lazy(routeLoaders['/user-dashboard']);
const BlogPage = lazy(routeLoaders['/blog']);
const BlogPostPage = lazy(() => import('./pages/blog/[slug]'));
const ServiceAreasPage = lazy(routeLoaders['/service-areas']);
const DaytonServiceAreaPage = lazy(routeLoaders['/service-areas/ohio/dayton']);
const ColumbusServiceAreaPage = lazy(routeLoaders['/service-areas/ohio/columbus']);
const CincinnatiServiceAreaPage = lazy(routeLoaders['/service-areas/ohio/cincinnati']);
const DallasServiceAreaPage = lazy(routeLoaders['/service-areas/texas/dallas']);
const NationalMapPage = lazy(routeLoaders['/national-map']);
const AdminCityAnalytics = lazy(routeLoaders['/admin/city-analytics']);
const WelcomePage = lazy(routeLoaders['/welcome']);
// RETIRED 2026-07-09: investor feature unused; share_investments locked by RLS. Do not re-enable without security review.
// import InvestPage from './pages/invest';
const PitchDeckPage = lazy(routeLoaders['/pitch-deck']);
const InsurancePage = lazy(routeLoaders['/insurance']);
const CompareQuotePage = lazy(routeLoaders['/compare/quote']);
const SharedQuoteReportPage = lazy(routeLoaders['/compare/quote/shared']);

// The router-agnostic route tree. Exported separately so the build-time
// prerenderer (src/prerender/entry-server.jsx) can render the EXACT same
// tree under a StaticRouter — any drift between the server-rendered HTML
// and the first client render would break React 18 hydration on the
// prerendered marketing pages. The prefetch installers live here (not in
// the default wrapper) so they keep running client-side; they are inside
// useEffect, which never runs during react-dom/server rendering.
export const SiteRoutes = () => {
  useEffect(() => {
    // Keep client-side navigation feeling instant after code-splitting:
    // warm a route's chunk on link hover/focus/touch (desktop intent), on
    // scroll-into-view (mobile — no hover exists; ~90% of traffic), and
    // idle-prefetch the top-of-funnel route after first paint.
    const removeLinkPrefetch = installLinkPrefetch();
    const removeViewportPrefetch = installViewportPrefetch();
    const cancelIdlePrefetch = prefetchHighIntentWhenIdle();
    return () => {
      removeLinkPrefetch();
      removeViewportPrefetch();
      cancelIdlePrefetch();
    };
  }, []);

  return (
    <ErrorBoundary>
      <ScrollToTop />
      {/* Route-derived canonical + og:url/twitter:url for EVERY route (see
          components/CanonicalTags.jsx). Mounted here, inside the router and
          above the route table, so it is impossible for a new page to ship
          without one — which is exactly how 11 routes, the homepage included,
          ended up emitting no canonical at all. */}
      <CanonicalTags />
      <SEO />
      <Suspense fallback={<BrandedSplash />}>
        <RouterRoutes>
              <Route path="/" element={<Homepage />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy-choices" element={<PrivacyChoices />} />
              <Route path="/for-contractors" element={<ForContractorsPage />} />
              <Route path="/financing-options" element={<FinancingOptionsPage />} />
              <Route path="/instant-estimate" element={<InstantEstimate />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/warranty" element={<WarrantyPage />} />
              <Route path="/shingles" element={<ShinglesPage />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/user-registration" element={<UserRegistration />} />
              <Route path="/user-dashboard" element={<UserDashboard />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/service-areas" element={<ServiceAreasPage />} />
              <Route path="/service-areas/ohio/dayton" element={<DaytonServiceAreaPage />} />
              <Route path="/service-areas/ohio/columbus" element={<ColumbusServiceAreaPage />} />
              <Route path="/service-areas/ohio/cincinnati" element={<CincinnatiServiceAreaPage />} />
              <Route path="/service-areas/texas/dallas" element={<DallasServiceAreaPage />} />
              <Route path="/national-map" element={<NationalMapPage />} />
              <Route path="/admin/city-analytics" element={<AdminCityAnalytics />} />
              <Route path="/welcome" element={<WelcomePage />} />
              {/* RETIRED 2026-07-09: investor feature unused; share_investments locked by RLS. Do not re-enable without security review. */}
              {/* <Route path="/invest" element={<InvestPage />} /> */}
              <Route path="/pitch-deck" element={<PitchDeckPage />} />
              <Route path="/insurance" element={<InsurancePage />} />
              {/* /compare (bare) is the static SEO hub in public/compare/ —
                  nginx serves it before the SPA fallback, so the interactive
                  quote-comparison tool lives at /compare/quote. */}
              <Route path="/compare/quote" element={<CompareQuotePage />} />
              {/* Shared (redacted) Quote Check report — PRD §5.9 K-factor
                  wave. Read-only; the backend strips contractor identity and
                  exact address before this page ever sees the payload. */}
              <Route path="/compare/quote/shared/:token" element={<SharedQuoteReportPage />} />
              <Route path="/installer-signup" element={<Navigate to="/for-contractors" replace />} />
              <Route path="/installer-signup-landing" element={<Navigate to="/for-contractors" replace />} />
              {/* Referral share links (openbooq.com/r/<code>, backend
                  buildLink format) redirect to the canonical /?ref=<code>
                  redemption form — see pages/referral-redirect. */}
              <Route path="/r/:code" element={<ReferralRedirect />} />
              <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </Suspense>
    </ErrorBoundary>
  );
};

// Browser wrapper — what the client entry mounts. HelmetProvider satisfies
// react-helmet-async consumers; the page-level <Helmet> tags (plain
// react-helmet) need no provider. Kept as a thin shell so SiteRoutes stays
// the single source of truth for the route table.
const Routes = () => (
  <HelmetProvider>
    <BrowserRouter>
      <SiteRoutes />
    </BrowserRouter>
  </HelmetProvider>
);

export default Routes;
